import {
    ADataGridColumn,
    ADataGridFilter,
} from '@atiautomacao/ati-ui-library';
import * as XLSX from 'xlsx';
import {
    TELEOBJECT_PAGE_SIZES,
    TeleobjectFilters,
    TeleobjectInhibitionRow,
    TeleobjectPageSize,
    TeleobjectSearchRequest,
    TeleobjectSort,
    TeleobjectSortField,
} from './Teleobjects.types';

export const TELEOBJECT_GRID_COLUMNS = [
    {
        name: 'teleObjectName',
        label: 'Teleobjeto',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {
            name: 'teleObjectName',
            condition: 'contains',
            value: '',
            sort: 'asc',
        },
    },
    {
        name: 'equipmentName',
        label: 'Equipamento',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {
            name: 'equipmentName',
            condition: 'contains',
            value: '',
            sort: 'none',
        },
    },
    {
        name: 'powerStationName',
        label: 'Usina',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {
            name: 'powerStationName',
            condition: 'contains',
            value: '',
            sort: 'none',
        },
    },
    {
        name: 'skidName',
        label: 'Skid',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {
            name: 'skidName',
            condition: 'contains',
            value: '',
            sort: 'none',
        },
    },
    {
        name: 'inhibited',
        label: 'Situação',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {
            name: 'inhibited',
            condition: 'contains',
            value: '',
            sort: 'none',
        },
    },
    {
        name: 'actions',
        label: 'Ações',
        align: 'center',
        visibility: true,
        minWidth: 80,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {
            name: 'actions',
            condition: 'contains',
            value: '',
            sort: '',
        },
    },
] as ADataGridColumn[];

const INITIAL_FILTERS: TeleobjectFilters = {
    teleObjectName: '',
    equipmentName: '',
    powerStationName: '',
    skidName: '',
    inhibited: null,
};

const INITIAL_SORT: TeleobjectSort = {
    field: 'teleObjectName',
    direction: 'asc',
};

type TextFilterField =
    | 'teleObjectName'
    | 'equipmentName'
    | 'powerStationName'
    | 'skidName';

const TEXT_FILTERS: TextFilterField[] = [
    'teleObjectName',
    'equipmentName',
    'powerStationName',
    'skidName',
];

const isTextFilter = (name: string): name is TextFilterField =>
    TEXT_FILTERS.includes(name as TextFilterField);

const SORT_FIELDS: TeleobjectSortField[] = [
    'teleObjectName',
    'equipmentName',
    'powerStationName',
    'skidName',
    'inhibited',
];

export function parseInhibitedFilter(value: string): boolean | null {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
        return null;
    }
    if (normalized === 'true') {
        return true;
    }
    if (normalized === 'false') {
        return false;
    }

    const matchesInibido =
        normalized.length >= 3 &&
        ('inibido'.includes(normalized) || normalized.includes('inibido'));
    const matchesHabilitado =
        normalized.length >= 3 &&
        ('habilitado'.includes(normalized) ||
            normalized.includes('habilitado'));

    if (matchesInibido && !matchesHabilitado) {
        return true;
    }
    if (matchesHabilitado && !matchesInibido) {
        return false;
    }
    return null;
}

export function toTeleobjectSearchRequest(
    filters: ADataGridFilter[],
    page: number,
    size: number,
): TeleobjectSearchRequest {
    const nextFilters: TeleobjectFilters = {...INITIAL_FILTERS};
    let sort: TeleobjectSort = INITIAL_SORT;

    filters.forEach(filter => {
        if (filter.name === 'inhibited') {
            nextFilters.inhibited = parseInhibitedFilter(filter.value);
        } else if (isTextFilter(filter.name)) {
            nextFilters[filter.name] = filter.value ?? '';
        }

        if (
            (filter.sort === 'asc' || filter.sort === 'desc') &&
            SORT_FIELDS.includes(filter.name as TeleobjectSortField)
        ) {
            sort = {
                field: filter.name as TeleobjectSortField,
                direction: filter.sort,
            };
        }
    });

    const pageSize = TELEOBJECT_PAGE_SIZES.includes(size as TeleobjectPageSize)
        ? (size as TeleobjectPageSize)
        : 10;

    return {
        filters: nextFilters,
        pagination: {page, size: pageSize},
        sort,
    };
}

export type TeleobjectExportRow = {
    Teleobjeto: string;
    Equipamento: string;
    Usina: string;
    Skid: string;
    Situação: string;
};

export function buildTeleobjectExportRows(
    rows: TeleobjectInhibitionRow[],
): TeleobjectExportRow[] {
    return rows.map(row => ({
        Teleobjeto: row.teleObjectName,
        Equipamento: row.equipmentName,
        Usina: row.powerStationName,
        Skid: row.skidName ?? '',
        Situação: row.inhibited ? 'Inibido' : 'Habilitado',
    }));
}

export function downloadTeleobjectExport(
    rows: TeleobjectInhibitionRow[],
    format: 'csv' | 'pdf',
) {
    if (format === 'pdf') {
        printTeleobjectPdf(rows);
        return;
    }

    const worksheet = XLSX.utils.json_to_sheet(buildTeleobjectExportRows(rows));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Teleobjetos');
    XLSX.writeFile(workbook, 'teleobjetos.csv');
}

const EXPORT_COLUMNS: Array<keyof TeleobjectExportRow> = [
    'Teleobjeto',
    'Equipamento',
    'Usina',
    'Skid',
    'Situação',
];

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export function printTeleobjectPdf(rows: TeleobjectInhibitionRow[]) {
    const exportRows = buildTeleobjectExportRows(rows);
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        return;
    }

    const header = EXPORT_COLUMNS.map(
        column => `<th>${escapeHtml(column)}</th>`,
    ).join('');
    const body = exportRows
        .map(
            row =>
                `<tr>${EXPORT_COLUMNS.map(
                    column => `<td>${escapeHtml(row[column])}</td>`,
                ).join('')}</tr>`,
        )
        .join('');

    printWindow.document.write(`<!doctype html>
<html>
  <head>
    <title>Teleobjetos</title>
    <style>
      body { font-family: Arial, sans-serif; }
      h1 { font-size: 18px; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
      th { background: #1976d2; color: #fff; }
    </style>
  </head>
  <body>
    <h1>Inibe/Habilita Teleobjetos</h1>
    <table>
      <thead><tr>${header}</tr></thead>
      <tbody>${body}</tbody>
    </table>
  </body>
</html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
}
